/**
 * Seasonal themes: a theme that new sessions start on during a date window.
 *
 * Windows are inclusive and use the visitor's local date. Seasons limited to
 * certain countries are matched by the browser's time zone. A seasonal theme
 * only applies if it is enabled (see featureFlags.js). When windows overlap,
 * the first matching entry wins.
 */
import { THEME_LIST } from './themes';

// IANA zones (canonical and legacy aliases) for the US and its territories
const US_TIME_ZONES = new Set([
  'America/New_York', 'America/Detroit', 'America/Kentucky/Louisville',
  'America/Kentucky/Monticello', 'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Indiana/Marengo',
  'America/Indiana/Petersburg', 'America/Indiana/Vevay', 'America/Chicago',
  'America/Indiana/Tell_City', 'America/Indiana/Knox', 'America/Menominee',
  'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah', 'America/Denver', 'America/Boise', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'America/Juneau', 'America/Sitka',
  'America/Metlakatla', 'America/Yakutat', 'America/Nome', 'America/Adak',
  'Pacific/Honolulu',
  'America/Puerto_Rico', 'America/St_Thomas', 'Pacific/Guam', 'Pacific/Saipan',
  'Pacific/Pago_Pago',
  'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'US/Alaska', 'US/Hawaii',
  'US/Arizona', 'US/Michigan', 'US/Aleutian', 'US/East-Indiana', 'US/Indiana-Starke',
  'US/Samoa', 'America/Indianapolis', 'America/Louisville', 'America/Fort_Wayne',
  'America/Knox_IN', 'America/Virgin', 'Navajo',
]);

const countryFromTimeZone = (timeZone) => (US_TIME_ZONES.has(timeZone) ? 'US' : null);

const fixedWindow = (startMonth, startDay, endMonth, endDay) => (year) => ({
  start: new Date(year, startMonth - 1, startDay),
  end: new Date(year, endMonth - 1, endDay),
});

// Nov 1 through the Saturday after US Thanksgiving (4th Thursday of November);
// always ends Nov 24-30, so it never overlaps December
const harvestSeason = (year) => {
  const firstThursday = 1 + ((4 - new Date(year, 10, 1).getDay() + 7) % 7);
  const thanksgiving = firstThursday + 21;
  return {
    start: new Date(year, 10, 1),
    end: new Date(year, 10, thanksgiving + 2),
  };
};

export const SEASONAL_THEMES = [
  { themeId: 'trickOrTreat', window: fixedWindow(10, 24, 10, 31) },
  { themeId: 'harvest', countries: ['US'], window: harvestSeason },
  { themeId: 'yuletide', window: fixedWindow(12, 1, 12, 26) },
  { themeId: 'oldGlory', countries: ['US'], window: fixedWindow(7, 3, 7, 4) },
];

const dayNumber = (d) => d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

/**
 * Returns the seasonal theme active on `date` for a visitor in `timeZone`, or null.
 * `seasonKey` identifies this season instance (e.g. 'trickOrTreat-2026') so a
 * user's opt-out applies to the current year only.
 */
export function getActiveSeason(
  date = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) {
  const year = date.getFullYear();
  const today = dayNumber(date);
  const country = countryFromTimeZone(timeZone);

  const entry = SEASONAL_THEMES.find((s) => {
    if (s.countries && !s.countries.includes(country)) return false;
    if (!THEME_LIST.some((t) => t.id === s.themeId)) return false;
    const { start, end } = s.window(year);
    return today >= dayNumber(start) && today <= dayNumber(end);
  });

  return entry ? { themeId: entry.themeId, seasonKey: `${entry.themeId}-${year}` } : null;
}
